import { create } from "zustand";
import {
  type Trip,
  type POI,
  type Schedule,
  type TravelMode,
  type TripDefaultTravelMode,
  type ScheduledPOI,
  type TripNote,
  type ChangelogEvent,
  type ChangelogAction,
  type EntityType,
  type TripScheduleData,
  type PermissionLevel,
  type AccommodationConfig,
  type ZonedTimeString,
  type TripLeg,
  normalizeTripTimezoneIds,
} from "@poi-planner/shared";
import { useUserStore } from "./userStore";
import { useUIStore } from "./uiStore";
import { useLibraryStore } from "./libraryStore";
import { useTripsListStore } from "./tripsListStore";
import { legsCoveringDate, useLegStore } from "./legStore";
import { isOfflineMode, useConnectivityStore } from "./connectivityStore";
import { api, isTripRevisionConflict } from "../services/api";
import {
  getTrip as getSavedTrip,
  type SavedTrip,
} from "../services/offlineStore";
import { seedTravelRoutes } from "../services/travelService";
import {
  clearGranularRequests,
  trackGranularRequest,
  waitForGranularRequests,
} from "../services/granularSaveBarrier";
import { AUTO_SAVE_DELAY_MS } from "@poi-planner/shared";
import { tripTravelModePreferences } from "../utils/geo";
import {
  addCalendarDays,
  addDaysToDate,
  addMinutes,
  daysBetweenDates,
  zonedDate,
  zonedTimeOfDay,
} from "../utils/zonedTime";

export type SaveStatus = "saved" | "saving" | "error" | "unsaved";

interface StateSnapshot {
  trip: Trip | null;
  schedule: Schedule;
  // Pins and legs are granular data that never rides the full-trip save, but
  // schedule edits mutate them as a side effect (a drop auto-pins and assigns
  // a leg). Undo has to restore them too or those side effects accumulate.
  pinnedPoiIds: string[];
  legs: TripLeg[];
}

// Undo/redo state
interface UndoEntry {
  before: StateSnapshot;
  after: StateSnapshot;
  summary: string;
}

const MAX_UNDO_STACK = 50;
let undoStack: UndoEntry[] = [];
let redoStack: UndoEntry[] = [];

export interface ScheduledPoiTimeUpdate {
  startTime?: ZonedTimeString;
  endTime?: ZonedTimeString;
}

interface TripState {
  // Data
  trip: Trip | null;
  schedule: Schedule;
  permission: PermissionLevel | "owner" | null;
  pinnedPoiIds: Set<string>;

  // UI state
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  // Changelog
  pendingChangelog: ChangelogEvent[];

  // Getters
  isReadOnly: () => boolean;

  // Actions
  loadTrip: (tripId: string) => Promise<boolean>; // Returns true if found
  /** Open the copy saved on this device (offline, read-only). */
  loadTripFromSnapshot: (record: SavedTrip) => void;
  refreshTrip: (tripId: string) => Promise<boolean>;
  unloadTrip: () => void;
  setTripData: (data: TripScheduleData) => void;

  // Pinned POI actions
  pinPoi: (poiId: string) => Promise<boolean>;
  unpinPoi: (poiId: string) => Promise<boolean>;
  unpinFromTrip: (scheduledPoiId: string) => void;

  // Schedule mutations
  schedulePOI: (poiId: string, startTime: ZonedTimeString) => void;
  unschedulePOI: (scheduledPoiId: string) => void;
  updateScheduledPoi: (
    scheduledPoiId: string,
    updates: ScheduledPoiTimeUpdate,
  ) => void;
  setScheduledPoiLocked: (scheduledPoiId: string, locked: boolean) => void;

  // Note mutations
  createNote: (note: Omit<TripNote, "id">) => string;
  updateNote: (noteId: string, updates: Partial<Omit<TripNote, "id">>) => void;
  /**
   * Fold a revision returned by a granular route (legs, pins) into the cached
   * trip so the next full autosave does not send a stale one.
   */
  applyGranularRevision: (tripId: string, revision: number) => void;
  /** Drop a deleted leg from every note, mirroring the server-side scrub. */
  stripLegFromNotes: (legId: string) => void;
  deleteNote: (noteId: string) => void;

  // Trip/day mutations
  setSleep: (date: string, poiId: string | null) => void;
  setSleepAssignments: (updates: Record<string, string | null>) => void;
  /** Override (or with null, clear) the travel mode for arriving at an item. */
  setTravelMode: (targetId: string, mode: TravelMode | null) => void;
  setDayTimezone: (date: string, zone: string | null) => void;
  setTripTimezones: (defaultTimezone: string, timezoneIds: string[]) => void;
  /** Trip preferences for automatically chosen travel-pin modes. */
  setTripTravelDefaults: (defaults: {
    defaultTravelMode?: TripDefaultTravelMode;
    walkThresholdKm?: number;
  }) => void;
  setTripDateRange: (startDate: string, endDate: string) => void;

  // Accommodation config mutations
  setAccommodationConfig: (
    poiId: string,
    config: Partial<AccommodationConfig>,
  ) => void;
  getAccommodationConfig: (poiId: string) => AccommodationConfig | undefined;

  // Undo/Redo actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Derived data helpers (delegate to libraryStore)
  getAccommodation: (id: string) => POI | undefined;
  getPOI: (id: string) => POI | undefined;
  getUnscheduledPOIs: () => POI[];
}

// --- Auto-save state ---
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingFlush: Promise<void> | null = null;
const pendingPinRequests = new Map<string, Promise<boolean>>();

const pinRequestKey = (tripId: string, poiId: string) => `${tripId}:${poiId}`;

async function assignPoiForScheduledDate(
  tripId: string,
  poiId: string,
  date: string,
): Promise<void> {
  if (!(await useTripStore.getState().pinPoi(poiId))) return;
  if (useTripStore.getState().trip?.id !== tripId) return;

  const covering = legsCoveringDate(useLegStore.getState().legs, date);
  if (covering.length === 1) {
    await useLegStore.getState().addPoisToLeg(tripId, covering[0].id, [poiId]);
    return;
  }
  if (covering.length > 1) {
    useUIStore.getState().openPoiLegPicker({
      kind: "schedule-overlap",
      poiId,
      candidateLegIds: covering.map((leg) => leg.id),
    });
  }
}

/**
 * Assign a POI to a leg, pinning it to the trip first when needed.
 *
 * The leg membership route rejects POIs that are not pinned, so a sidebar
 * place dropped straight onto a leg bar has to be pinned the same way a
 * calendar drop pins it. Returns false when either step fails; the caller
 * surfaces legStore.error.
 */
export async function pinAndAssignPoiToLeg(
  tripId: string,
  legId: string,
  poiId: string,
): Promise<boolean> {
  return trackGranularRequest(
    tripId,
    (async () => {
      if (!(await useTripStore.getState().pinPoi(poiId))) {
        useLegStore.setState({
          error: "Could not add that place to the trip. Please try again.",
        });
        return false;
      }
      if (useTripStore.getState().trip?.id !== tripId) return false;
      return useLegStore.getState().addPoisToLeg(tripId, legId, [poiId]);
    })(),
  );
}

async function doSave(): Promise<void> {
  const initialTrip = useTripStore.getState().trip;
  if (!initialTrip) return;
  // Offline edits stay in memory; resumeTripSave sends them on reconnect.
  if (isOfflineMode()) {
    useTripStore.setState({ saveStatus: "unsaved" });
    return;
  }

  // Scheduling can auto-pin, assign legs, or edit a leg at nearly the same
  // time as this save. Every granular route bumps trips.revision, so let all
  // of them finish first or this snapshot carries a stale revision, 409s, and
  // the conflict path below discards the user's unsaved schedule edits.
  await waitForGranularRequests(initialTrip.id);

  const { trip, schedule, pendingChangelog } = useTripStore.getState();
  if (!trip || trip.id !== initialTrip.id) return;

  useTripStore.setState({ saveStatus: "saving" });
  try {
    const revision = await api.saveTripById(trip.id, { trip, schedule });
    useTripStore.setState((state) => ({
      trip:
        state.trip?.id === trip.id ? { ...state.trip, revision } : state.trip,
    }));

    if (pendingChangelog.length > 0) {
      await api.saveTripChangelog(trip.id, pendingChangelog);
      useTripStore.setState({ pendingChangelog: [] });
    }

    // Update trip summary in list
    const { permission } = useTripStore.getState();
    useTripsListStore.getState().updateTripSummary({
      id: trip.id,
      name: trip.name,
      startDate: trip.startDate,
      endDate: trip.endDate,
      dayCount: daysBetweenDates(trip.startDate, trip.endDate) + 1,
      createdAt: trip.createdAt,
      updatedAt: trip.updatedAt,
      revision,
      isOwner: permission === "owner",
      permission: permission || "owner",
    });

    useTripStore.setState({ saveStatus: "saved" });
  } catch (error) {
    if (isOfflineMode()) {
      // The connection dropped mid-save; keep the edits for the retry.
      useTripStore.setState({ saveStatus: "unsaved" });
      return;
    }
    if (isTripRevisionConflict(error)) {
      const latest = await api.loadTripById(trip.id).catch(() => null);
      if (latest) {
        useTripStore.setState({
          trip: normalizeTrip(latest.trip),
          schedule: normalizeSchedule(latest.schedule),
          pendingChangelog: [],
          saveStatus: "saved",
        });
        // The reload is authoritative for legs too; they ride the same GET.
        useLegStore.getState().setLegs(latest.legs);
        undoStack = [];
        redoStack = [];
        return;
      }
    }
    useTripStore.setState({ saveStatus: "error" });
  }
}

function queueSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  useTripStore.setState({ saveStatus: "unsaved" });

  saveTimeout = setTimeout(() => {
    saveTimeout = null;
    pendingFlush = doSave().finally(() => {
      pendingFlush = null;
    });
  }, AUTO_SAVE_DELAY_MS);
}

// --- Changelog helper ---
function addChangelog(
  action: ChangelogAction,
  entityType: EntityType,
  entityId: string,
  summary: string,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
) {
  const userState = useUserStore.getState();
  const user = userState.getDisplayName();
  const userId = userState.getAuthUserId();

  const event: ChangelogEvent = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    user,
    userId,
    action,
    entityType,
    entityId,
    summary,
    before,
    after,
    undoOf: null,
  };
  const pending = useTripStore.getState().pendingChangelog;
  useTripStore.setState({ pendingChangelog: [...pending, event] });
}

function normalizeSchedule(schedule: Schedule): Schedule {
  return {
    scheduledPOIs: (schedule.scheduledPOIs || []).map((sp) => ({
      ...sp,
      id: sp.id || crypto.randomUUID(),
      locked: sp.locked ?? false,
    })),
    notes: (schedule.notes || []).map((note) => ({
      ...note,
      id: note.id || crypto.randomUUID(),
      tagIds: note.tagIds ?? [],
      allDayStartDate: note.allDayStartDate ?? null,
      allDayEndDate: note.allDayEndDate ?? null,
      locked: note.locked ?? false,
      legIds: note.legIds ?? [],
    })),
    accommodationConfigs: schedule.accommodationConfigs ?? {},
    sleep: schedule.sleep ?? {},
    timezoneOverrides: schedule.timezoneOverrides ?? {},
    travelModes: schedule.travelModes ?? {},
  };
}

function normalizeTrip(trip: Trip): Trip {
  return {
    ...trip,
    timezoneIds: normalizeTripTimezoneIds(
      trip.defaultTimezone,
      trip.timezoneIds,
    ),
  };
}

function showLockedPrompt(
  message = "Unlock this item before changing its time.",
) {
  useUIStore.getState().showLockedActionPrompt(message);
}

/** Format a zoned time for changelog summaries: "2026-10-14 10:12" */
function summarizeTime(value: ZonedTimeString): string {
  return `${zonedDate(value)} ${zonedTimeOfDay(value)}`;
}

/** Shift a sparse date-keyed map by whole days. */
function shiftMapKeys(
  map: Record<string, string>,
  deltaDays: number,
): Record<string, string> {
  if (deltaDays === 0) return { ...map };
  const shifted: Record<string, string> = {};
  for (const [date, value] of Object.entries(map)) {
    shifted[addDaysToDate(date, deltaDays)] = value;
  }
  return shifted;
}

/** Prune map entries whose date key falls outside [startDate, endDate]. */
function pruneMapToRange(
  map: Record<string, string>,
  startDate: string,
  endDate: string,
): Record<string, string> {
  const pruned: Record<string, string> = {};
  for (const [date, value] of Object.entries(map)) {
    if (date >= startDate && date <= endDate) {
      pruned[date] = value;
    }
  }
  return pruned;
}

/** Legs the POI currently belongs to, captured before an optimistic strip. */
function legIdsContainingPoi(poiId: string): string[] {
  return useLegStore
    .getState()
    .legs.filter((leg) => leg.poiIds.includes(poiId))
    .map((leg) => leg.id);
}

// --- Undo/Redo helpers ---
let _isUndoRedo = false;
// Held for the whole async granular replay below, where _isUndoRedo (cleared
// synchronously after the setState) has already gone false.
let _isReplayingGranular = false;
let _beforeSnapshot: StateSnapshot | null = null;

function captureSnapshot(): StateSnapshot {
  const { trip, schedule, pinnedPoiIds } = useTripStore.getState();
  const { legs } = useLegStore.getState();
  return JSON.parse(
    JSON.stringify({ trip, schedule, pinnedPoiIds: [...pinnedPoiIds], legs }),
  );
}

function capturingUndo() {
  return !_isUndoRedo && !_isReplayingGranular;
}

function beginMutation() {
  if (capturingUndo()) {
    _beforeSnapshot = captureSnapshot();
  }
}

function endMutation(summary: string) {
  if (capturingUndo() && _beforeSnapshot) {
    const after = captureSnapshot();
    undoStack.push({ before: _beforeSnapshot, after, summary });
    if (undoStack.length > MAX_UNDO_STACK) {
      undoStack.shift();
    }
    redoStack = [];
    _beforeSnapshot = null;
  }
}

/**
 * Record a leg edit on the undo stack.
 *
 * Leg definitions are granular data, so `apply` must perform the optimistic
 * local update synchronously — the snapshot pair is taken around it, before
 * the network request settles, exactly like the schedule mutations.
 *
 * Leg creation and deletion deliberately stay off the stack: neither can be
 * reversed from the client (POST assigns a fresh id), and deletion is
 * reversible through the Activity Feed's id-preserving `restore_leg`.
 */
export function recordUndoableLegChange(summary: string, apply: () => void) {
  beginMutation();
  apply();
  endMutation(summary);
}

/** Drop note references to legs that no longer exist. */
function sanitizeScheduleLegIds(
  schedule: Schedule,
  liveLegIds: Set<string>,
): Schedule {
  if (
    !schedule.notes.some((note) =>
      note.legIds.some((legId) => !liveLegIds.has(legId)),
    )
  ) {
    return schedule;
  }
  return {
    ...schedule,
    notes: schedule.notes.map((note) =>
      note.legIds.every((legId) => liveLegIds.has(legId))
        ? note
        : { ...note, legIds: note.legIds.filter((id) => liveLegIds.has(id)) },
    ),
  };
}

/**
 * Replay the granular half of an undo/redo: pins and leg membership are not
 * part of the full-trip snapshot, so restoring `trip`/`schedule` alone would
 * leave behind the pin and leg assignment a scheduling drop created.
 *
 * Legs deleted since the snapshot are skipped rather than recreated — see
 * recordUndoableLegChange.
 */
async function restoreGranularState(
  from: StateSnapshot,
  to: StateSnapshot,
  liveLegIds: Set<string>,
): Promise<void> {
  const tripId = useTripStore.getState().trip?.id;
  if (!tripId) return;

  _isReplayingGranular = true;
  try {
    const fromPins = new Set(from.pinnedPoiIds);
    const targetPins = new Set(to.pinnedPoiIds);
    const store = useTripStore.getState();

    await Promise.all([
      ...to.pinnedPoiIds
        .filter((poiId) => !fromPins.has(poiId))
        .map((poiId) => store.pinPoi(poiId)),
      // Unpinning cascades leg membership away server-side, so it has to
      // settle before membership below is reconciled.
      ...from.pinnedPoiIds
        .filter((poiId) => !targetPins.has(poiId))
        .map((poiId) => store.unpinPoi(poiId)),
    ]);

    if (useTripStore.getState().trip?.id !== tripId) return;

    const legStore = useLegStore.getState();
    for (const targetLeg of to.legs) {
      if (!liveLegIds.has(targetLeg.id)) continue;
      const live = legStore.legs.find((leg) => leg.id === targetLeg.id);
      if (!live) continue;

      if (
        live.name !== targetLeg.name ||
        live.startDate !== targetLeg.startDate ||
        live.endDate !== targetLeg.endDate ||
        live.color !== targetLeg.color
      ) {
        await legStore.updateLeg(tripId, targetLeg.id, {
          name: targetLeg.name,
          startDate: targetLeg.startDate,
          endDate: targetLeg.endDate,
          color: targetLeg.color,
        });
      }

      // A POI the target snapshot has unpinned cannot hold membership.
      const desired = targetLeg.poiIds.filter((poiId) => targetPins.has(poiId));
      const desiredSet = new Set(desired);
      const current = useLegStore
        .getState()
        .legs.find((leg) => leg.id === targetLeg.id);
      if (!current) continue;

      const missing = desired.filter(
        (poiId) => !current.poiIds.includes(poiId),
      );
      if (missing.length > 0) {
        await legStore.addPoisToLeg(tripId, targetLeg.id, missing);
      }
      for (const poiId of current.poiIds) {
        if (!desiredSet.has(poiId)) {
          await legStore.removePoiFromLeg(tripId, targetLeg.id, poiId);
        }
      }
    }
  } finally {
    _isReplayingGranular = false;
  }
}

/** Move the store to a snapshot, restoring granular state alongside it. */
function applySnapshot(target: StateSnapshot) {
  const current = captureSnapshot();
  const liveLegIds = new Set(useLegStore.getState().legs.map((leg) => leg.id));

  _isUndoRedo = true;
  useTripStore.setState({
    trip: target.trip,
    schedule: sanitizeScheduleLegIds(target.schedule, liveLegIds),
  });
  _isUndoRedo = false;

  const tripId = useTripStore.getState().trip?.id;
  if (tripId) {
    void trackGranularRequest(
      tripId,
      restoreGranularState(current, target, liveLegIds),
    );
  }
}

/**
 * Viewers and offline sessions are read-only. A new function identity is set
 * whenever connectivity changes so `s.isReadOnly` selectors re-render.
 */
function readOnlyCheck(): () => boolean {
  return () =>
    useTripStore.getState().permission === "viewer" || isOfflineMode();
}

// Default empty schedule
const emptySchedule: Schedule = {
  scheduledPOIs: [],
  notes: [],
  accommodationConfigs: {},
  sleep: {},
  timezoneOverrides: {},
  travelModes: {},
};

export const useTripStore = create<TripState>((set, get) => ({
  trip: null,
  schedule: emptySchedule,
  permission: null,
  pinnedPoiIds: new Set<string>(),
  loading: false,
  error: null,
  saveStatus: "saved" as SaveStatus,
  pendingChangelog: [],

  isReadOnly: readOnlyCheck(),

  loadTrip: async (tripId: string) => {
    set({ loading: true, error: null });
    if (isOfflineMode()) return loadSavedTrip(tripId);
    try {
      const [data, permission, pinnedIds] = await Promise.all([
        api.loadTripById(tripId),
        api.getTripPermission(tripId),
        api.getPinnedPoiIds(tripId).catch(() => [] as string[]),
      ]);

      if (!data) {
        set({ trip: null, permission: null, loading: false });
        return false;
      }

      const schedule = normalizeSchedule(data.schedule);

      set({
        trip: normalizeTrip(data.trip),
        schedule,
        permission,
        pinnedPoiIds: new Set(pinnedIds),
        loading: false,
        pendingChangelog: [],
      });
      // Legs come back on the same GET, so no extra round trip is needed.
      useLegStore.getState().setLegs(data.legs);

      // Reset undo/redo stacks when loading a new trip
      undoStack = [];
      redoStack = [];
      return true;
    } catch (err) {
      // A network failure flips the app offline; fall back to the saved copy.
      if (isOfflineMode()) return loadSavedTrip(tripId);
      set({ error: (err as Error).message, loading: false });
      return false;
    }
  },

  loadTripFromSnapshot: (record: SavedTrip) => {
    set({
      trip: normalizeTrip(record.trip),
      schedule: normalizeSchedule(record.schedule),
      permission: record.permission,
      pinnedPoiIds: new Set(record.pinnedPoiIds),
      loading: false,
      error: null,
      pendingChangelog: [],
      saveStatus: "saved",
    });
    useLegStore.getState().setLegs(record.legs);
    if (record.travelRoutes) seedTravelRoutes(record.travelRoutes);
    undoStack = [];
    redoStack = [];
  },

  refreshTrip: async (tripId: string) => {
    try {
      const [data, permission, pinnedIds] = await Promise.all([
        api.loadTripById(tripId),
        api.getTripPermission(tripId),
        api.getPinnedPoiIds(tripId).catch(() => [] as string[]),
      ]);

      if (!data) return false;

      set({
        trip: normalizeTrip(data.trip),
        schedule: normalizeSchedule(data.schedule),
        permission,
        pinnedPoiIds: new Set(pinnedIds),
        pendingChangelog: [],
        error: null,
      });
      useLegStore.getState().setLegs(data.legs);
      undoStack = [];
      redoStack = [];
      return true;
    } catch {
      return false;
    }
  },

  unloadTrip: () => {
    const unloadedTripId = get().trip?.id ?? null;
    // Cancel any pending saves
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    set({
      trip: null,
      schedule: emptySchedule,
      permission: null,
      pinnedPoiIds: new Set<string>(),
      pendingChangelog: [],
      saveStatus: "saved",
    });
    useLegStore.getState().reset();
    useUIStore.getState().closePoiLegPicker();
    if (unloadedTripId) clearGranularRequests(unloadedTripId);
    pendingPinRequests.clear();
    undoStack = [];
    redoStack = [];
  },

  setTripData: (data: TripScheduleData) => {
    const schedule = normalizeSchedule(data.schedule);
    set({
      trip: normalizeTrip(data.trip),
      schedule,
    });
  },

  // --- Schedule mutations ---

  schedulePOI: (poiId: string, startTime: ZonedTimeString) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();
    const poi = pois.find((p) => p.id === poiId);
    if (!poi) return;

    beginMutation();

    const endTime = addMinutes(startTime, poi.timeBudget || 60);
    const scheduledPoi: ScheduledPOI = {
      id: crypto.randomUUID(),
      poiId,
      startTime,
      endTime,
      locked: false,
    };

    set({
      schedule: {
        ...schedule,
        scheduledPOIs: [...schedule.scheduledPOIs, scheduledPoi],
      },
    });
    addChangelog("schedule", "poi", poiId, `Scheduled "${poi.name}"`, null, {
      startTime,
      endTime,
    });
    queueSave();
    endMutation(`Scheduled "${poi.name}"`);

    const currentTrip = get().trip;
    if (currentTrip) {
      void trackGranularRequest(
        currentTrip.id,
        assignPoiForScheduledDate(currentTrip.id, poiId, zonedDate(startTime)),
      );
    }
  },

  unschedulePOI: (scheduledPoiId: string) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();

    const beforeSp = schedule.scheduledPOIs.find(
      (sp) => sp.id === scheduledPoiId,
    );
    if (!beforeSp) return;
    if (beforeSp.locked) {
      showLockedPrompt("Unlock this scheduled POI before unscheduling it.");
      return;
    }

    const poi = pois.find((p) => p.id === beforeSp.poiId);

    beginMutation();
    set({
      schedule: {
        ...schedule,
        scheduledPOIs: schedule.scheduledPOIs.filter(
          (sp) => sp.id !== scheduledPoiId,
        ),
      },
    });
    addChangelog(
      "unschedule",
      "poi",
      beforeSp.poiId,
      `Unscheduled "${poi?.name ?? beforeSp.poiId}"`,
      { scheduledPoiId, startTime: beforeSp.startTime },
      null,
    );
    queueSave();
    endMutation(`Unscheduled "${poi?.name ?? beforeSp.poiId}"`);

    // Clear target if this POI was targeted
    const targetPoiId = useUIStore.getState().targetPoiId;
    if (targetPoiId === beforeSp.poiId) {
      useUIStore.getState().setTargetPoiId(null);
    }
  },

  updateScheduledPoi: (
    scheduledPoiId: string,
    updates: ScheduledPoiTimeUpdate,
  ) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();

    const sp = schedule.scheduledPOIs.find((s) => s.id === scheduledPoiId);
    if (!sp) return;
    if (sp.locked) {
      showLockedPrompt("Unlock this scheduled POI before changing its time.");
      return;
    }

    const startTime = updates.startTime ?? sp.startTime;
    const endTime = updates.endTime ?? sp.endTime;
    if (startTime === sp.startTime && endTime === sp.endTime) return;

    const poi = pois.find((p) => p.id === sp.poiId);

    beginMutation();
    set({
      schedule: {
        ...schedule,
        scheduledPOIs: schedule.scheduledPOIs.map((s) =>
          s.id === scheduledPoiId ? { ...s, startTime, endTime } : s,
        ),
      },
    });
    addChangelog(
      "update",
      "poi",
      sp.poiId,
      `Moved "${poi?.name ?? sp.poiId}" to ${summarizeTime(startTime)}`,
      { startTime: sp.startTime, endTime: sp.endTime },
      { startTime, endTime },
    );
    queueSave();
    endMutation(`Moved "${poi?.name ?? sp.poiId}"`);

    const trip = get().trip;
    if (trip && zonedDate(startTime) !== zonedDate(sp.startTime)) {
      void trackGranularRequest(
        trip.id,
        assignPoiForScheduledDate(trip.id, sp.poiId, zonedDate(startTime)),
      );
    }
  },

  setScheduledPoiLocked: (scheduledPoiId: string, locked: boolean) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();

    const sp = schedule.scheduledPOIs.find((s) => s.id === scheduledPoiId);
    if (!sp || sp.locked === locked) return;

    const poi = pois.find((p) => p.id === sp.poiId);

    beginMutation();
    set({
      schedule: {
        ...schedule,
        scheduledPOIs: schedule.scheduledPOIs.map((s) =>
          s.id === scheduledPoiId ? { ...s, locked } : s,
        ),
      },
    });
    addChangelog(
      "update",
      "poi",
      sp.poiId,
      `${locked ? "Locked" : "Unlocked"} "${poi?.name ?? sp.poiId}"`,
      { locked: sp.locked },
      { locked },
    );
    queueSave();
    endMutation(`${locked ? "Locked" : "Unlocked"} "${poi?.name ?? sp.poiId}"`);
  },

  // --- Note mutations ---

  createNote: (noteData: Omit<TripNote, "id">) => {
    beginMutation();
    const id = crypto.randomUUID();
    const note: TripNote = { ...noteData, id };
    const { schedule } = get();

    set({ schedule: { ...schedule, notes: [...schedule.notes, note] } });
    addChangelog("create", "note", id, `Created note "${note.name}"`, null, {
      name: note.name,
      startTime: note.startTime,
      endTime: note.endTime,
      allDayStartDate: note.allDayStartDate,
      allDayEndDate: note.allDayEndDate,
    });
    queueSave();
    endMutation(`Created note "${note.name}"`);
    return id;
  },

  updateNote: (noteId: string, updates: Partial<Omit<TripNote, "id">>) => {
    const { schedule } = get();
    const before = schedule.notes.find((n) => n.id === noteId);
    if (!before) return;

    const changesTime =
      (updates.startTime !== undefined &&
        updates.startTime !== before.startTime) ||
      (updates.endTime !== undefined && updates.endTime !== before.endTime);
    const changesAllDayDates =
      (updates.allDayStartDate !== undefined &&
        updates.allDayStartDate !== before.allDayStartDate) ||
      (updates.allDayEndDate !== undefined &&
        updates.allDayEndDate !== before.allDayEndDate);
    if (
      before.locked &&
      (changesTime || changesAllDayDates) &&
      updates.locked !== false
    ) {
      showLockedPrompt("Unlock this note before changing its time.");
      return;
    }

    beginMutation();
    const updated: TripNote = { ...before, ...updates, id: noteId };
    set({
      schedule: {
        ...schedule,
        notes: schedule.notes.map((n) => (n.id === noteId ? updated : n)),
      },
    });
    addChangelog(
      "update",
      "note",
      noteId,
      `Updated note "${updated.name}"`,
      {
        name: before.name,
        startTime: before.startTime,
        endTime: before.endTime,
        allDayStartDate: before.allDayStartDate,
        allDayEndDate: before.allDayEndDate,
      },
      {
        name: updated.name,
        startTime: updated.startTime,
        endTime: updated.endTime,
        allDayStartDate: updated.allDayStartDate,
        allDayEndDate: updated.allDayEndDate,
      },
    );
    queueSave();
    endMutation(`Updated note "${updated.name}"`);
  },

  deleteNote: (noteId: string) => {
    const { schedule } = get();
    const note = schedule.notes.find((n) => n.id === noteId);
    if (!note) return;
    if (note.locked) {
      showLockedPrompt("Unlock this note before deleting it.");
      return;
    }

    beginMutation();
    set({
      schedule: {
        ...schedule,
        notes: schedule.notes.filter((n) => n.id !== noteId),
      },
    });
    addChangelog(
      "delete",
      "note",
      noteId,
      `Deleted note "${note.name}"`,
      { name: note.name, startTime: note.startTime, endTime: note.endTime },
      null,
    );
    queueSave();
    endMutation(`Deleted note "${note.name}"`);
  },

  // --- Trip/day mutations ---

  setSleep: (date: string, poiId: string | null) => {
    const { schedule } = get();

    beginMutation();
    const beforePoiId = schedule.sleep[date] ?? null;
    const newSleep = { ...schedule.sleep };
    if (poiId) {
      newSleep[date] = poiId;
    } else {
      delete newSleep[date];
    }

    // Auto-create accommodation config if not exists
    const newConfigs = { ...schedule.accommodationConfigs };
    if (poiId && !newConfigs[poiId]) {
      // Find next available color (count existing configs)
      const usedColors = Object.values(newConfigs).map((c) => c.color);
      let nextColor = 0;
      for (let i = 0; i < 8; i++) {
        if (!usedColors.includes(i)) {
          nextColor = i;
          break;
        }
      }
      newConfigs[poiId] = {
        color: nextColor,
        checkInTime: "15:00",
        checkOutTime: "11:00",
      };
    }

    set({
      schedule: {
        ...schedule,
        sleep: newSleep,
        accommodationConfigs: newConfigs,
      },
    });
    addChangelog(
      "sleep_change",
      "trip",
      date,
      `Changed sleep assignment for ${date}`,
      { poiId: beforePoiId },
      { poiId },
    );
    queueSave();
    endMutation("Changed sleep assignment");
  },

  setSleepAssignments: (updates: Record<string, string | null>) => {
    const { schedule, trip } = get();
    const entries = Object.entries(updates).filter(
      ([date, poiId]) => (schedule.sleep[date] ?? null) !== poiId,
    );
    if (!entries.length) return;

    beginMutation();
    const nextSleep = { ...schedule.sleep };
    const nextConfigs = { ...schedule.accommodationConfigs };
    const before: Record<string, string | null> = {};
    for (const [date, poiId] of entries) {
      before[date] = schedule.sleep[date] ?? null;
      if (poiId) {
        nextSleep[date] = poiId;
        if (!nextConfigs[poiId]) {
          const used = Object.values(nextConfigs).map((config) => config.color);
          const color =
            Array.from({ length: 8 }, (_, index) => index).find(
              (index) => !used.includes(index),
            ) ?? 0;
          nextConfigs[poiId] = {
            color,
            checkInTime: "15:00",
            checkOutTime: "11:00",
          };
        }
      } else {
        delete nextSleep[date];
      }
    }
    set({
      schedule: {
        ...schedule,
        sleep: nextSleep,
        accommodationConfigs: nextConfigs,
      },
    });
    addChangelog(
      "sleep_change",
      "trip",
      trip?.id ?? "trip",
      `Changed ${entries.length} accommodation night${entries.length === 1 ? "" : "s"}`,
      before,
      Object.fromEntries(entries),
    );
    queueSave();
    endMutation("Changed accommodation stay");
  },

  setTravelMode: (targetId: string, mode: TravelMode | null) => {
    const { schedule, trip } = get();
    if (!trip) return;

    const current = schedule.travelModes ?? {};
    const before = current[targetId] ?? null;
    if (before === mode) return;

    beginMutation();
    const travelModes = { ...current };
    if (mode) {
      travelModes[targetId] = mode;
    } else {
      delete travelModes[targetId];
    }

    set({ schedule: { ...schedule, travelModes } });
    addChangelog(
      "update",
      "trip",
      targetId,
      `Changed travel mode to ${mode ?? "automatic"}`,
      { travelMode: before ?? "auto" },
      { travelMode: mode ?? "auto" },
    );
    queueSave();
    endMutation("Changed travel mode");
  },

  setDayTimezone: (date: string, zone: string | null) => {
    const { schedule, trip } = get();
    if (!trip) return;

    const beforeZone = schedule.timezoneOverrides[date] ?? null;
    const normalized = zone === trip.defaultTimezone ? null : zone;
    if (beforeZone === normalized) return;

    beginMutation();
    const newOverrides = { ...schedule.timezoneOverrides };
    if (normalized) {
      newOverrides[date] = normalized;
    } else {
      delete newOverrides[date];
    }

    set({ schedule: { ...schedule, timezoneOverrides: newOverrides } });
    addChangelog(
      "update",
      "trip",
      date,
      `Changed timezone for ${date} to ${zone ?? trip.defaultTimezone}`,
      { timezone: beforeZone ?? trip.defaultTimezone },
      { timezone: zone ?? trip.defaultTimezone },
    );
    queueSave();
    endMutation(`Changed timezone for ${date}`);
  },

  setTripTimezones: (defaultTimezone: string, timezoneIds: string[]) => {
    const { trip, schedule, isReadOnly } = get();
    if (!trip || isReadOnly()) return;

    const normalizedIds = normalizeTripTimezoneIds(
      defaultTimezone,
      timezoneIds,
    );
    const unchanged =
      trip.defaultTimezone === defaultTimezone &&
      trip.timezoneIds.length === normalizedIds.length &&
      trip.timezoneIds.every((zone, index) => zone === normalizedIds[index]);
    if (unchanged) return;

    beginMutation();
    const timezoneOverrides =
      trip.defaultTimezone === defaultTimezone
        ? schedule.timezoneOverrides
        : Object.fromEntries(
            Object.entries(schedule.timezoneOverrides).filter(
              ([, zone]) => zone !== defaultTimezone,
            ),
          );
    set({
      trip: { ...trip, defaultTimezone, timezoneIds: normalizedIds },
      schedule: { ...schedule, timezoneOverrides },
    });
    addChangelog(
      "update",
      "trip",
      trip.id,
      "Updated trip timezones",
      {
        defaultTimezone: trip.defaultTimezone,
        timezoneIds: trip.timezoneIds,
      },
      { defaultTimezone, timezoneIds: normalizedIds },
    );
    queueSave();
    endMutation("Updated trip timezones");
  },

  setTripTravelDefaults: (defaults) => {
    const { trip, isReadOnly } = get();
    if (!trip || isReadOnly()) return;

    const before = tripTravelModePreferences(trip);
    const after = { ...before, ...defaults };
    if (
      after.defaultTravelMode === before.defaultTravelMode &&
      after.walkThresholdKm === before.walkThresholdKm
    ) {
      return;
    }

    beginMutation();
    set({ trip: { ...trip, ...after } });
    addChangelog(
      "update",
      "trip",
      trip.id,
      "Updated trip travel defaults",
      { ...before },
      { ...after },
    );
    queueSave();
    endMutation("Updated trip travel defaults");
  },

  setTripDateRange: (startDate: string, endDate: string) => {
    const { trip, schedule } = get();
    if (!trip) return;
    if (startDate > endDate) return;
    if (trip.startDate === startDate && trip.endDate === endDate) return;

    const deltaDays = daysBetweenDates(trip.startDate, startDate);

    // A start shift rewrites every block's date; locked blocks pin their times
    const hasLockedBlocks =
      schedule.scheduledPOIs.some((sp) => sp.locked) ||
      schedule.notes.some(
        (n) => n.locked && (n.startTime || n.allDayStartDate),
      );
    if (deltaDays !== 0 && hasLockedBlocks) {
      showLockedPrompt(
        "Unlock scheduled items before shifting this trip's start date.",
      );
      return;
    }

    const shiftZoned = (value: ZonedTimeString) =>
      deltaDays === 0 ? value : addCalendarDays(value, deltaDays);

    const shiftedPois = schedule.scheduledPOIs.map((sp) => ({
      ...sp,
      startTime: shiftZoned(sp.startTime),
      endTime: shiftZoned(sp.endTime),
    }));
    const shiftedNotes = schedule.notes.map((note) => {
      if (note.startTime && note.endTime) {
        return {
          ...note,
          startTime: shiftZoned(note.startTime),
          endTime: shiftZoned(note.endTime),
        };
      }
      if (note.allDayStartDate && note.allDayEndDate && deltaDays !== 0) {
        return {
          ...note,
          allDayStartDate: addDaysToDate(note.allDayStartDate, deltaDays),
          allDayEndDate: addDaysToDate(note.allDayEndDate, deltaDays),
        };
      }
      return note;
    });

    // Shrinking the range deletes the blocks on dropped dates. The date-range
    // picker (milestone 5) confirms with the user before calling this.
    const inRange = (start: ZonedTimeString) => {
      const date = zonedDate(start);
      return date >= startDate && date <= endDate;
    };
    const droppedPois = shiftedPois.filter((sp) => !inRange(sp.startTime));
    const droppedNotes = shiftedNotes.filter((note) => {
      if (note.startTime) return !inRange(note.startTime);
      if (note.allDayStartDate && note.allDayEndDate) {
        return note.allDayStartDate < startDate || note.allDayEndDate > endDate;
      }
      return false;
    });
    if (
      droppedPois.some((sp) => sp.locked) ||
      droppedNotes.some((n) => n.locked)
    ) {
      showLockedPrompt(
        "Unlock scheduled items on the removed dates before shrinking this trip.",
      );
      return;
    }

    beginMutation();

    const newSleep = pruneMapToRange(
      shiftMapKeys(schedule.sleep, deltaDays),
      startDate,
      endDate,
    );
    // Extending the end carries the last night's accommodation forward
    // (matching the old addDay behavior)
    const prevEndDate = addDaysToDate(trip.endDate, deltaDays);
    const lastNightPoiId = newSleep[prevEndDate];
    if (lastNightPoiId && endDate > prevEndDate) {
      for (
        let date = addDaysToDate(prevEndDate, 1);
        date <= endDate;
        date = addDaysToDate(date, 1)
      ) {
        newSleep[date] = lastNightPoiId;
      }
    }
    const newOverrides = pruneMapToRange(
      shiftMapKeys(schedule.timezoneOverrides, deltaDays),
      startDate,
      endDate,
    );

    const keptPois = shiftedPois.filter((sp) => inRange(sp.startTime));
    const keptNotes = shiftedNotes.filter((note) => {
      if (note.startTime) return inRange(note.startTime);
      if (note.allDayStartDate && note.allDayEndDate) {
        return (
          note.allDayStartDate >= startDate && note.allDayEndDate <= endDate
        );
      }
      return true;
    });
    // Travel-mode overrides are keyed by item id; drop those whose item the
    // shrink removed.
    const keptIds = new Set([
      ...keptPois.map((sp) => sp.id),
      ...keptNotes.map((note) => note.id),
    ]);
    const travelModes = Object.fromEntries(
      Object.entries(schedule.travelModes ?? {}).filter(([id]) =>
        keptIds.has(id),
      ),
    );

    set({
      trip: { ...trip, startDate, endDate },
      schedule: {
        ...schedule,
        scheduledPOIs: keptPois,
        notes: keptNotes,
        sleep: newSleep,
        timezoneOverrides: newOverrides,
        travelModes,
      },
    });
    addChangelog(
      "update",
      "trip",
      trip.id,
      `Changed trip dates to ${startDate} – ${endDate}`,
      { startDate: trip.startDate, endDate: trip.endDate },
      { startDate, endDate },
    );
    queueSave();
    endMutation(`Changed trip dates to ${startDate} – ${endDate}`);
  },

  // --- Accommodation config mutations ---

  setAccommodationConfig: (
    poiId: string,
    config: Partial<AccommodationConfig>,
  ) => {
    const { schedule } = get();
    const existing = schedule.accommodationConfigs[poiId] ?? { color: 0 };
    const updated = { ...existing, ...config };
    const newConfigs = { ...schedule.accommodationConfigs, [poiId]: updated };

    set({ schedule: { ...schedule, accommodationConfigs: newConfigs } });
    queueSave();
  },

  getAccommodationConfig: (poiId: string) => {
    return get().schedule.accommodationConfigs[poiId];
  },

  applyGranularRevision: (tripId, revision) => {
    set((state) =>
      state.trip?.id === tripId
        ? {
            trip: {
              ...state.trip,
              revision: Math.max(state.trip.revision, revision),
            },
          }
        : ({} as Partial<TripState>),
    );
  },

  stripLegFromNotes: (legId) => {
    const { schedule } = get();
    if (!schedule.notes.some((note) => note.legIds.includes(legId))) return;
    // Deliberately no changelog or undo entry: this mirrors a scrub the server
    // already performed, so it is bookkeeping rather than a user edit. It does
    // need a save, or the next autosave would write the dangling id back.
    set({
      schedule: {
        ...schedule,
        notes: schedule.notes.map((note) =>
          note.legIds.includes(legId)
            ? { ...note, legIds: note.legIds.filter((id) => id !== legId) }
            : note,
        ),
      },
    });
    queueSave();
  },

  // --- Pinned POI actions ---

  pinPoi: async (poiId: string) => {
    const { trip, pinnedPoiIds } = get();
    if (!trip) return false;

    const key = pinRequestKey(trip.id, poiId);
    const pending = pendingPinRequests.get(key);
    if (pending) return pending;
    if (pinnedPoiIds.has(poiId)) return true;

    // Optimistic update
    const newPinned = new Set(pinnedPoiIds);
    newPinned.add(poiId);
    set({ pinnedPoiIds: newPinned });

    const request = trackGranularRequest(
      trip.id,
      api.pinPoisToTrip(trip.id, [poiId]),
    )
      .then((revision) => {
        get().applyGranularRevision(trip.id, revision);
        return true;
      })
      .catch(() => {
        // Rollback on error
        set((state) => {
          if (state.trip?.id !== trip.id) return state;
          const rolled = new Set(state.pinnedPoiIds);
          rolled.delete(poiId);
          return { pinnedPoiIds: rolled };
        });
        return false;
      })
      .finally(() => {
        pendingPinRequests.delete(key);
      });
    pendingPinRequests.set(key, request);
    return request;
  },

  unpinPoi: async (poiId: string) => {
    let { trip, pinnedPoiIds } = get();
    if (!trip) return false;
    const initialTripId = trip.id;

    const pending = pendingPinRequests.get(pinRequestKey(initialTripId, poiId));
    if (pending && !(await pending)) return false;

    ({ trip, pinnedPoiIds } = get());
    if (!trip || trip.id !== initialTripId) return false;
    if (!pinnedPoiIds.has(poiId)) return true;
    const tripId = trip.id;
    const previousLegIds = legIdsContainingPoi(poiId);

    // Optimistic update
    const newPinned = new Set(pinnedPoiIds);
    newPinned.delete(poiId);
    set({ pinnedPoiIds: newPinned });
    useLegStore.getState().stripPoiFromLegs(poiId);

    return trackGranularRequest(tripId, api.unpinPoisFromTrip(tripId, [poiId]))
      .then((revision) => {
        get().applyGranularRevision(tripId, revision);
        return true;
      })
      .catch(() => {
        // Rollback on error
        set((state) => {
          if (state.trip?.id !== tripId) return state;
          const rolled = new Set(state.pinnedPoiIds);
          rolled.add(poiId);
          return { pinnedPoiIds: rolled };
        });
        useLegStore.getState().restorePoiToLegs(poiId, previousLegIds);
        return false;
      });
  },

  unpinFromTrip: (scheduledPoiId: string) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();

    const beforeSp = schedule.scheduledPOIs.find(
      (sp) => sp.id === scheduledPoiId,
    );
    if (!beforeSp) return;
    if (beforeSp.locked) {
      showLockedPrompt(
        "Unlock this scheduled POI before removing it from the trip.",
      );
      return;
    }

    const poi = pois.find((p) => p.id === beforeSp.poiId);

    beginMutation();
    set({
      schedule: {
        ...schedule,
        scheduledPOIs: schedule.scheduledPOIs.filter(
          (sp) => sp.id !== scheduledPoiId,
        ),
      },
    });
    addChangelog(
      "unschedule",
      "poi",
      beforeSp.poiId,
      `Unscheduled and unpinned "${poi?.name ?? beforeSp.poiId}"`,
      { scheduledPoiId, startTime: beforeSp.startTime },
      null,
    );
    queueSave();
    endMutation(`Unscheduled and unpinned "${poi?.name ?? beforeSp.poiId}"`);

    // Clear target if this POI was targeted
    const targetPoiId = useUIStore.getState().targetPoiId;
    if (targetPoiId === beforeSp.poiId) {
      useUIStore.getState().setTargetPoiId(null);
    }

    // Also unpin
    const { trip, pinnedPoiIds } = get();
    if (trip && pinnedPoiIds.has(beforeSp.poiId)) {
      const newPinned = new Set(pinnedPoiIds);
      newPinned.delete(beforeSp.poiId);
      set({ pinnedPoiIds: newPinned });
      // Unpinning cascades trip_leg_pois away server-side; mirror it locally.
      const legIdsBefore = legIdsContainingPoi(beforeSp.poiId);
      useLegStore.getState().stripPoiFromLegs(beforeSp.poiId);

      const tripId = trip.id;
      void trackGranularRequest(
        tripId,
        api.unpinPoisFromTrip(tripId, [beforeSp.poiId]),
      )
        .then((revision) => {
          get().applyGranularRevision(tripId, revision);
        })
        .catch(() => {
          // Rollback pin state on error
          set((state) => {
            const rolled = new Set(state.pinnedPoiIds);
            rolled.add(beforeSp.poiId);
            return { pinnedPoiIds: rolled };
          });
          useLegStore.getState().restorePoiToLegs(beforeSp.poiId, legIdsBefore);
        });
    }
  },

  // --- Undo/Redo actions ---

  undo: () => {
    if (get().isReadOnly()) return;
    const entry = undoStack.pop();
    if (!entry) return;

    redoStack.push(entry);
    applySnapshot(entry.before);

    const user = useUserStore.getState().displayName || "Unknown";
    const event: ChangelogEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user,
      action: "undo",
      entityType: "trip",
      entityId: "",
      summary: `Undo: ${entry.summary}`,
      before: null,
      after: null,
      undoOf: null,
    };
    const pending = get().pendingChangelog;
    set({ pendingChangelog: [...pending, event] });
    queueSave();
  },

  redo: () => {
    if (get().isReadOnly()) return;
    const entry = redoStack.pop();
    if (!entry) return;

    undoStack.push(entry);
    applySnapshot(entry.after);

    const user = useUserStore.getState().displayName || "Unknown";
    const event: ChangelogEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user,
      action: "redo",
      entityType: "trip",
      entityId: "",
      summary: `Redo: ${entry.summary}`,
      before: null,
      after: null,
      undoOf: null,
    };
    const pending = get().pendingChangelog;
    set({ pendingChangelog: [...pending, event] });
    queueSave();
  },

  canUndo: () => undoStack.length > 0,
  canRedo: () => redoStack.length > 0,

  // --- Derived data helpers ---

  getAccommodation: (id: string) => {
    return useLibraryStore.getState().getAccommodation(id);
  },

  getPOI: (id: string) => {
    return useLibraryStore.getState().getPOI(id);
  },

  getUnscheduledPOIs: () => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();
    const scheduledIds = new Set(schedule.scheduledPOIs.map((sp) => sp.poiId));
    return pois.filter((p) => !scheduledIds.has(p.id));
  },
}));

async function loadSavedTrip(tripId: string): Promise<boolean> {
  const record = await getSavedTrip(tripId);
  if (!record) {
    useTripStore.setState({ trip: null, permission: null, loading: false });
    return false;
  }
  useTripStore.getState().loadTripFromSnapshot(record);
  return true;
}

useConnectivityStore.subscribe((state, prev) => {
  if (state.mode !== prev.mode) {
    useTripStore.setState({ isReadOnly: readOnlyCheck() });
  }
});

/** Send edits that were queued while the connection was down. */
export async function resumeTripSave(): Promise<void> {
  const { trip, saveStatus } = useTripStore.getState();
  if (!trip || saveStatus === "saved" || saveStatus === "saving") return;
  if (saveTimeout) return; // A debounced save is already on its way.
  pendingFlush = doSave().finally(() => {
    pendingFlush = null;
  });
  await pendingFlush;
}

// Export flush function for external use
export async function flushTripSave(): Promise<void> {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
    await doSave();
  } else if (pendingFlush) {
    await pendingFlush;
  }
}
